import { Controller } from 'stimulus';
import { triggerCustomEvent } from 'lib/helpers';
import SearchStateManager from 'lib/search-state';
import { ezLoadingBtnOn, ezLoadingBtnOff } from 'ez-styles/assets/js/utils/buttons';
import { ajaxGet } from 'lib/ajax-helpers';
import { scrolledIntoView } from 'lib/scroll-helpers';
import frameDebounce from 'lib/frame-debounce';
import events from 'lib/events';

const searchState = new SearchStateManager();

export default class InfiniteScrollController extends Controller {
  infiniteScrollActivated = false;

  endOfResultsReached = false;

  prevSearchPage = null;

  prevShowPage = null;

  isFetching = false;

  loadingIndicator = null;

  backToTop = null;

  connect() {
    window.addEventListener('scroll', this.debouncedScrollCheck);

    if (this.element.dataset.startInfiniteScrollOnLoad === 'true') {
      this.startInfiniteScroll();
    }
  }

  disconnect() {
    window.removeEventListener('scroll', this.debouncedScrollCheck);
  }

  normalizeTerm(term) {
    return encodeURIComponent(term.toLowerCase().trim().replace(/ /g, '-'));
  }

  showSpinner(element) {
    if (element && !element.classList.contains('has-spinner')) {
      this.disableWithAttr = element.getAttribute('data-disable-with');
      element.removeAttribute('data-disable-with');

      ezLoadingBtnOn(element);
    }
  }

  hideSpinner(element) {
    if (element && element.classList.contains('has-spinner')) {
      ezLoadingBtnOff(element);

      element.setAttribute('data-disable-with', this.disableWithAttr);
    }
  }

  infiniteScrollCheck = () => {
    if (this.infiniteScrollActivated) {
      // check for more results
      if (
        !this.endOfResultsReached
        && !this.isFetching
        && this.loadingIndicator
        && scrolledIntoView(this.loadingIndicator, 1400)
      ) {
        this.loadMoreSearchResults();
      }

      // check for back to top button hide/show
      const docViewTop = window.pageYOffset || document.documentElement.scrollTop;

      if (
        docViewTop < 400
      && !this.backToTop.classList.contains('is-hidden')
      ) {
        this.backToTop.classList.add('is-hidden');
      } else if (
        docViewTop > 800
        && this.backToTop.classList.contains('is-hidden')
      ) {
        triggerCustomEvent('hidePromoPopup');
        this.backToTop.classList.remove('is-hidden');
      }
    }
  }

  debouncedScrollCheck = frameDebounce(this.infiniteScrollCheck);

  getSearchResultsUrl() {
    const { protocol, host } = window.location;
    const currentPage = searchState.getRawStateObject().page;
    const { contentType } = document.querySelector('#search').dataset;
    this.prevSearchPage = currentPage;
    searchState.updateStateValue('page', currentPage + 1);
    const searchStateQueryString = searchState.getFlatQueryString();
    const queryString = searchStateQueryString.substr(1);

    return `${protocol}//${host}/${this.searchRoute}?qterm=${this.searchTerm}&${queryString}&content_type=${contentType}&ajax=true`;
  }

  getShowResultsUrl() {
    const { protocol, host, pathname } = window.location;
    const currentPage = this.prevShowPage ? this.prevShowPage + 1 : 1;
    this.prevShowPage = currentPage; // This seems terrible :/
    const queryString = `page=${currentPage + 1}`;

    return `${protocol}//${host}${pathname}?${queryString}&ajax=true`;
  }

  loadMoreSearchResults = (event) => {
    let loadMoreButton;
    this.isFetching = true;

    // only do spinner changes on load more click
    if (event) {
      loadMoreButton = event.target;
      this.showSpinner(loadMoreButton);
      triggerCustomEvent(events.vertical_grid.show_more_click, {
        pageType: this.pageType,
      });
    }

    let resultsUrl = '';
    if (this.isSearchPage) resultsUrl = this.getSearchResultsUrl();
    if (this.isShowPage) resultsUrl = this.getShowResultsUrl();

    ajaxGet(resultsUrl, (res) => {
      if (event) this.hideSpinner(loadMoreButton);
      this.insertAjaxResources(res, resultsUrl);
      this.isFetching = false;
    }, (err) => {
      if (this.isSearchPage) searchState.updateStateValue('page', this.prevSearchPage);
      console.error(err); // eslint-disable-line
      this.isFetching = false;
    });
  }

  insertAjaxResources = (res, fromUrl) => {
    const resultsBody = document.createElement('DIV');
    resultsBody.innerHTML = res.data;
    const resultsItems = resultsBody.querySelectorAll('ul.vertical-grid li');

    if (!this.infiniteScrollActivated) this.startInfiniteScroll();

    if (resultsItems.length) {
      resultsItems.forEach((item) => { this.searchResults.appendChild(item); });
      this.infiniteScrollCheck();
      triggerCustomEvent(events.vertical_grid.more_results_loaded, {
        url: fromUrl,
      });
    } else {
      this.stopInfiniteScroll();
      triggerCustomEvent(events.vertical_grid.end_of_results, {
        pageType: this.pageType,
        url: window.location.pathname,
      });
    }

    triggerCustomEvent(this.reloadGridEvent);
  }

  startInfiniteScroll = () => {
    this.createLoadingNotice();
    if (this.showMoreWrapper) this.showMoreWrapper.remove();
    triggerCustomEvent('StartResultsInfiniteScroll');

    if (this.isSearchPage) {
      window.addEventListener('beforeunload', () => {
        searchState.updateStateValue('page', 1); // reset to page one on reload
      });
    }

    this.createBackToTopButton();
    this.infiniteScrollActivated = true;
  }

  stopInfiniteScroll = () => {
    if (!this.loadingIndicator) {
      this.createLoadingNotice();
      this.showMoreWrapper.remove();
    }

    this.loadingIndicator.textContent = 'End of results';
    this.loadingIndicator.classList.add('search-results__loading-indicator--end');
    this.endOfResultsReached = true;
  }

  createLoadingNotice() {
    this.loadingIndicator = document.createElement('DIV');
    this.loadingIndicator.classList.add('search-results__loading-indicator');

    this.loadingIndicator.textContent = 'Loading more results';
    this.searchResults.insertAdjacentElement('afterend', this.loadingIndicator);
  }

  createBackToTopButton() {
    this.backToTop = document.createElement('DIV');
    this.backToTop.classList.add('search-results__back-to-top');
    this.backToTop.classList.add('is-hidden');
    this.backToTop.dataset.action = 'click->infinite-scroll#backToTopClick';

    const backToTopCircle = document.createElement('DIV');
    backToTopCircle.title = 'Back to top';
    backToTopCircle.classList.add('search-results__back-to-top__circle');
    this.backToTop.appendChild(backToTopCircle);

    this.loadingIndicator.insertAdjacentElement('beforebegin', this.backToTop);
  }

  backToTopClick = () => {
    window.scrollTo(0, 0);
  }

  get pageType() {
    let pageType = '';
    if (document.body.classList.contains('searches-show')) pageType = 'search';
    if (document.body.classList.contains('resources-show')) pageType = 'related';
    return pageType;
  }

  get searchRoute() {
    const searchInput = document.querySelector('#search');

    if (searchInput) return searchInput.dataset.searchRoute;
    return 'search';
  }

  get searchTerm() {
    const searchInfo = this.element.querySelector('[data-search-term]');

    if (searchInfo) return searchInfo.getAttribute('data-search-term');
    return '';
  }

  get searchResults() {
    let searchResults;

    if (this.isSearchPage) searchResults = this.element.querySelector('.ez-search-results ul.vertical-grid');
    if (this.isShowPage) searchResults = this.element.querySelector('.ez-resource-related ul.vertical-grid');

    if (searchResults) return searchResults;
    return null;
  }

  get showMoreWrapper() {
    const showMoreWrapper = this.element.querySelector('.search-results__show-more');

    if (showMoreWrapper) return showMoreWrapper;
    return null;
  }

  get isShowPage() {
    return document.body.classList.contains('resources-show');
  }

  get isSearchPage() {
    return document.body.classList.contains('searches-show');
  }

  get reloadGridEvent() {
    return this.element.dataset.reloadGridEvent;
  }
}
